import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { registerUser, getUsers, toggleUserStatus, wipeAndReseed as wipeAndReseedService, clearByType as clearByTypeService, resetBankBalances as resetBankBalancesService, updateCompanySettings } from '../services/settingsService';
import { swalSuccess, swalError } from '../utils/alert';
import './SettingsPage.css';

const SettingsPage = () => {
  const { user, sessionTimeout, updateSessionTimeout } = useAuth();
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [userIsSuperAdmin, setUserIsSuperAdmin] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [password, setPassword] = useState('');

  const [isClearTypeModalOpen, setClearTypeModalOpen] = useState(false);
  const [selectedClearType, setSelectedClearType] = useState(null);
  const [passwordForType, setPasswordForType] = useState('');

  const [selectedResetMode, setSelectedResetMode] = useState(null);
  const [passwordForReset, setPasswordForReset] = useState('');

  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState(sessionTimeout);

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const clearTypes = [
    { key: 'lancamentos',  label: 'Lançamentos',         description: 'Despesas, receitas, transferências e faturas' },
    { key: 'bancos',       label: 'Bancos',               description: 'Contas bancárias' },
    { key: 'cartoes',      label: 'Cartões de Crédito',   description: 'Cartões e faturas associadas' },
    { key: 'categorias',   label: 'Categorias',           description: 'Categorias de despesas e receitas' },
    { key: 'fornecedores', label: 'Fornecedores',         description: 'Cadastro de fornecedores' },
    { key: 'clientes',     label: 'Clientes',             description: 'Cadastro de clientes' },
    { key: 'recorrencias', label: 'Recorrências',         description: 'Lançamentos recorrentes programados' },
  ];

  const resetModes = [
    { key: 'zerar',      label: 'Zerar Saldos',            description: 'Zera o saldo dos bancos e remove os lançamentos de "Ajuste de saldo". Receitas e despesas reais são mantidas.', irreversible: true },
    { key: 'recalcular', label: 'Recalcular Saldos',       description: 'Recalcula o saldo de cada banco a partir das receitas recebidas, despesas e faturas pagas. Nada é excluído.', irreversible: false },
    { key: 'zerar_tudo', label: 'Zerar Saldos e Lançamentos', description: 'Zera o saldo e exclui receitas recebidas, despesas pagas e transferências dos bancos. Faturas de cartão são mantidas.', irreversible: true },
  ];

  const isSuperAdmin = user?.is_super_admin || user?.company_id === null;
  const companyId   = user?.company_id;
  const companyName = user?.company?.name || 'sua empresa';

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      swalError(error.message || 'Falha ao carregar usuários.');
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRegisterUser = async (e) => {
    e.preventDefault();
    try {
      const response = await registerUser({ name: userName, email: userEmail, password: userPassword, is_super_admin: userIsSuperAdmin });
      swalSuccess(response.message);
      setUserName(''); setUserEmail(''); setUserPassword(''); setUserIsSuperAdmin(false);
      loadUsers();
    } catch (error) {
      swalError(error.message || 'Ocorreu um erro ao registrar o usuário.');
    }
  };

  const handleToggleUser = async (targetUser) => {
    try {
      const response = await toggleUserStatus(targetUser.id, !targetUser.is_active);
      swalSuccess(response.message);
      setUsers(prev => prev.map(u => u.id === targetUser.id ? { ...u, is_active: !u.is_active } : u));
    } catch (error) {
      swalError(error.message || 'Falha ao atualizar status do usuário.');
    }
  };

  const handleWipeAndReseed = async () => {
    try {
      const response = await wipeAndReseedService(password, companyId);
      swalSuccess(response.message);
    } catch (error) {
      swalError(error.message || 'Ocorreu um erro ao limpar os dados da empresa.');
    } finally {
      setModalOpen(false);
      setPassword('');
    }
  };

  const handleSaveSessionTimeout = async () => {
    try {
      await updateCompanySettings({ session_timeout_minutes: sessionTimeoutMinutes });
      updateSessionTimeout(sessionTimeoutMinutes);
      swalSuccess('Tempo de sessão atualizado com sucesso.');
    } catch (error) {
      swalError(error.message || 'Falha ao salvar configurações.');
    }
  };

  const handleOpenClearTypeModal = (type) => {
    setSelectedClearType(type);
    setClearTypeModalOpen(true);
  };

  const handleClearByType = async () => {
    try {
      const response = await clearByTypeService(passwordForType, companyId, selectedClearType.key);
      swalSuccess(response.message);
    } catch (error) {
      swalError(error.message || 'Ocorreu um erro ao excluir os dados.');
    } finally {
      setClearTypeModalOpen(false);
      setPasswordForType('');
    }
  };

  const handleResetBankBalances = async () => {
    try {
      const response = await resetBankBalancesService(passwordForReset, companyId, selectedResetMode.key);
      swalSuccess(response.message);
    } catch (error) {
      swalError(error.message || 'Ocorreu um erro ao resetar os saldos.');
    } finally {
      setSelectedResetMode(null);
      setPasswordForReset('');
    }
  };

  return (
    <div className="settings-page">

      {/* ── Page Header ── */}
      <div className="sp-page-header">
        <div className="sp-page-header-icon">⚙️</div>
        <div>
          <h2>Configurações</h2>
          <p>Gerenciamento de usuários e dados do sistema</p>
        </div>
      </div>

      <div className="sp-content">

          {/* ── Modal: Excluir por Tipo ── */}
        {isClearTypeModalOpen && selectedClearType && (
          <div className="sp-modal-backdrop">
            <div className="sp-modal">
              <div className="sp-modal-head">
                <h4>⚠ Confirmar Exclusão</h4>
                <button className="sp-modal-x" onClick={() => setClearTypeModalOpen(false)}>✕</button>
              </div>
              <div className="sp-modal-body">
                <p className="sp-modal-warn">
                  Você está prestes a excluir permanentemente todos os{' '}
                  <strong>{selectedClearType.label}</strong> da empresa{' '}
                  <strong>{companyName}</strong>.{' '}
                  Esta ação é <strong>irreversível</strong>.
                </p>
                <div className="sp-modal-meta">{selectedClearType.description}</div>
                <div className="sp-form-group">
                  <label className="sp-label" htmlFor="adminPasswordType">Senha do administrador</label>
                  <input
                    type="password"
                    id="adminPasswordType"
                    value={passwordForType}
                    onChange={(e) => setPasswordForType(e.target.value)}
                    className="sp-input"
                    placeholder="••••••••"
                    autoFocus
                  />
                </div>
              </div>
              <div className="sp-modal-foot">
                <button className="sp-btn sp-btn-ghost" onClick={() => setClearTypeModalOpen(false)}>Cancelar</button>
                <button className="sp-btn sp-btn-danger" onClick={handleClearByType}>
                  Excluir {selectedClearType.label}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Modal: Resetar Saldos ── */}
        {selectedResetMode && (
          <div className="sp-modal-backdrop">
            <div className="sp-modal">
              <div className="sp-modal-head">
                <h4>⚠ Confirmar {selectedResetMode.label}</h4>
                <button className="sp-modal-x" onClick={() => setSelectedResetMode(null)}>✕</button>
              </div>
              <div className="sp-modal-body">
                <p className="sp-modal-warn">
                  Você está prestes a executar <strong>{selectedResetMode.label}</strong> nos bancos da empresa{' '}
                  <strong>{companyName}</strong>.{' '}
                  {selectedResetMode.irreversible && <>Esta ação é <strong>irreversível</strong>.</>}
                </p>
                <div className="sp-modal-meta">{selectedResetMode.description}</div>
                <div className="sp-form-group">
                  <label className="sp-label" htmlFor="adminPasswordReset">Senha do administrador</label>
                  <input
                    type="password"
                    id="adminPasswordReset"
                    value={passwordForReset}
                    onChange={(e) => setPasswordForReset(e.target.value)}
                    className="sp-input"
                    placeholder="••••••••"
                    autoFocus
                  />
                </div>
              </div>
              <div className="sp-modal-foot">
                <button className="sp-btn sp-btn-ghost" onClick={() => setSelectedResetMode(null)}>Cancelar</button>
                <button className="sp-btn sp-btn-danger" onClick={handleResetBankBalances}>
                  {selectedResetMode.label}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Modal: Limpar Tudo ── */}
        {isModalOpen && (
          <div className="sp-modal-backdrop">
            <div className="sp-modal">
              <div className="sp-modal-head">
                <h4>⚠ Ação Destrutiva</h4>
                <button className="sp-modal-x" onClick={() => setModalOpen(false)}>✕</button>
              </div>
              <div className="sp-modal-body">
                <p className="sp-modal-warn">
                  Esta ação apagará <strong>todos os dados</strong> da empresa{' '}
                  <strong>{companyName}</strong>, exceto usuários.{' '}
                  Esta ação é <strong>irreversível</strong>.
                </p>
                <div className="sp-form-group">
                  <label className="sp-label" htmlFor="adminPassword">Senha do administrador</label>
                  <input
                    type="password"
                    id="adminPassword"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="sp-input"
                    placeholder="••••••••"
                    autoFocus
                  />
                </div>
              </div>
              <div className="sp-modal-foot">
                <button className="sp-btn sp-btn-ghost" onClick={() => setModalOpen(false)}>Cancelar</button>
                <button className="sp-btn sp-btn-danger" onClick={handleWipeAndReseed}>
                  Limpar Todos os Dados
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Zona de Perigo (super admin only) ── */}
        {isSuperAdmin && (
          <>
            <p className="sp-section-label">Zona de Perigo</p>

            {/* Card: Excluir por Tipo */}
            <div className="sp-card danger sp-stagger-1">
              <div className="sp-card-head">
                <div className="sp-card-icon red">🗂</div>
                <div className="sp-card-head-text">
                  <h3>Excluir Dados por Tipo</h3>
                  <p>Remova seletivamente um tipo de dado de uma empresa</p>
                </div>
                <span className="sp-danger-badge">Perigo</span>
              </div>
              <div className="sp-card-body">
                <div className="sp-form-group">
                  <label className="sp-label">Empresa</label>
                  <p className="sp-input" style={{ margin: 0, background: '#f8fafc', cursor: 'default' }}>{companyName}</p>
                </div>
                <div className="sp-type-grid">
                  {clearTypes.map(type => (
                    <button key={type.key} className="sp-type-btn" onClick={() => handleOpenClearTypeModal(type)}>
                      <span className="sp-type-name">{type.label}</span>
                      <span className="sp-type-desc">{type.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Card: Resetar Saldos Bancários */}
            <div className="sp-card danger sp-stagger-2">
              <div className="sp-card-head">
                <div className="sp-card-icon red">🏦</div>
                <div className="sp-card-head-text">
                  <h3>Resetar Saldos Bancários</h3>
                  <p>Zere ou recalcule o saldo das contas bancárias de uma empresa</p>
                </div>
                <span className="sp-danger-badge">Perigo</span>
              </div>
              <div className="sp-card-body">
                <div className="sp-form-group">
                  <label className="sp-label">Empresa</label>
                  <p className="sp-input" style={{ margin: 0, background: '#f8fafc', cursor: 'default' }}>{companyName}</p>
                </div>
                <div className="sp-type-grid">
                  {resetModes.map(mode => (
                    <button key={mode.key} className="sp-type-btn" onClick={() => setSelectedResetMode(mode)}>
                      <span className="sp-type-name">{mode.label}</span>
                      <span className="sp-type-desc">{mode.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Card: Limpar Tudo */}
            <div className="sp-card danger sp-stagger-3">
              <div className="sp-card-head">
                <div className="sp-card-icon red">💣</div>
                <div className="sp-card-head-text">
                  <h3>Limpar Todos os Dados</h3>
                  <p>Remove todos os dados de uma empresa, exceto usuários</p>
                </div>
                <span className="sp-danger-badge">Perigo</span>
              </div>
              <div className="sp-card-body">
                <div className="sp-form-group">
                  <label className="sp-label">Empresa</label>
                  <p className="sp-input" style={{ margin: 0, background: '#f8fafc', cursor: 'default' }}>{companyName}</p>
                </div>
                <button
                  className="sp-wipe-btn"
                  onClick={() => setModalOpen(true)}
                >
                  ☠ Limpar Todos os Dados da Empresa
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── Tempo de Sessão ── */}
        <p className="sp-section-label">Segurança</p>

        <div className="sp-card sp-stagger-3">
          <div className="sp-card-head">
            <div className="sp-card-icon blue">⏱</div>
            <div className="sp-card-head-text">
              <h3>Tempo de Sessão por Inatividade</h3>
              <p>A sessão será encerrada automaticamente após este período sem interação</p>
            </div>
          </div>
          <div className="sp-card-body">
            <div className="d-flex align-items-center gap-3">
              <input
                type="number"
                className="sp-input"
                style={{ maxWidth: 110 }}
                min={1}
                max={480}
                value={sessionTimeoutMinutes}
                onChange={e => setSessionTimeoutMinutes(Number(e.target.value))}
              />
              <span className="text-muted">minutos</span>
              <button className="sp-btn sp-btn-primary" onClick={handleSaveSessionTimeout}>
                Salvar
              </button>
            </div>
          </div>
        </div>

        {/* ── Cadastro de Usuário ── */}
        <p className="sp-section-label">Gerenciar Usuários</p>

        <div className="sp-card sp-stagger-3">
          <div className="sp-card-head">
            <div className="sp-card-icon blue">👤</div>
            <div className="sp-card-head-text">
              <h3>Cadastrar Novo Usuário</h3>
              <p>Adicione um usuário ao sistema</p>
            </div>
          </div>
          <div className="sp-card-body">
            <form onSubmit={handleRegisterUser} className="sp-user-form">
              <div className="sp-form-group">
                <label className="sp-label" htmlFor="userName">Nome</label>
                <input
                  type="text"
                  id="userName"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  className="sp-input"
                  placeholder="Nome completo"
                  required
                />
              </div>
              <div className="sp-form-group">
                <label className="sp-label" htmlFor="userType">Tipo de Usuário</label>
                <select
                  id="userType"
                  className="sp-select"
                  value={userIsSuperAdmin ? 'super' : 'user'}
                  onChange={(e) => setUserIsSuperAdmin(e.target.value === 'super')}
                  disabled={!isSuperAdmin}
                >
                  <option value="user">Usuário Comum</option>
                  {isSuperAdmin && <option value="super">Super Usuário</option>}
                </select>
              </div>
              <div className="sp-form-group full">
                <label className="sp-label" htmlFor="userEmail">Email</label>
                <input
                  type="email"
                  id="userEmail"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  className="sp-input"
                  placeholder="usuario@empresa.com"
                  required
                />
              </div>
              <div className="sp-form-group full">
                <label className="sp-label" htmlFor="userPassword">Senha</label>
                <input
                  type="password"
                  id="userPassword"
                  value={userPassword}
                  onChange={(e) => setUserPassword(e.target.value)}
                  className="sp-input"
                  placeholder="••••••••"
                  required
                />
              </div>
              <div className="sp-form-actions">
                <button type="submit" className="sp-btn sp-btn-primary">
                  + Registrar Usuário
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* ── Lista de Usuários ── */}
        <div className="sp-card sp-stagger-4">
          <div className="sp-card-head">
            <div className="sp-card-icon blue">👥</div>
            <div className="sp-card-head-text">
              <h3>Usuários Cadastrados</h3>
              <p>Gerencie o acesso dos usuários ao sistema</p>
            </div>
          </div>
          <div className="sp-card-body">
            {usersLoading && <p className="sp-loading-text">Carregando usuários...</p>}
            {!usersLoading && users.length === 0 && (
              <p className="sp-empty-text">Nenhum usuário encontrado.</p>
            )}
            {!usersLoading && users.length > 0 && (
              <div className="sp-user-list">
                {users.map(u => (
                  <div key={u.id} className={`sp-user-item ${!u.is_active ? 'sp-user-inactive' : ''}`}>
                    <div className="sp-user-info">
                      <span className="sp-user-name">
                        {u.name}
                        {u.id === user?.id && <span className="sp-user-you"> (você)</span>}
                        {u.is_super_admin && <span className="sp-user-badge-admin">Super Admin</span>}
                      </span>
                      <span className="sp-user-email">{u.email}</span>
                      {isSuperAdmin && u.company && (
                        <span className="sp-user-company">{u.company.name}</span>
                      )}
                    </div>
                    <div className="sp-user-actions">
                      <span className={`sp-status-badge ${u.is_active ? 'active' : 'inactive'}`}>
                        {u.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                      {u.id !== user?.id && !u.is_super_admin && (
                        <button
                          className={`sp-btn sp-btn-sm ${u.is_active ? 'sp-btn-ghost' : 'sp-btn-primary'}`}
                          onClick={() => handleToggleUser(u)}
                        >
                          {u.is_active ? 'Desativar' : 'Ativar'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default SettingsPage;
